import Market from '@/creditflowresearch/domain/Market';
import Region from '@/creditflowresearch/domain/Region';
import { MarketIdByAbbreviation } from '@/creditflowresearch/domain/Market/constants';
import { SegmentTypeById } from '@/creditflowresearch/domain/Segment/constants';
import {
  getVisibleSegmentIds,
  type ISegmentScope,
} from '@/creditflowresearch/domain/Segment/selectors/selectSegmentVisibility';
import Preferences from '@/creditflowresearch/domain/Preferences/Preferences';
import CrossProductMappingService from '@/creditflowresearch/domain/Preferences/CrossProductMappingService';
import { PreferenceFamily } from '@/creditflowresearch/domain/Preferences/PreferenceSource';
import { hasRegionAccess, type IRegionAccessControl } from '@/creditflowresearch/domain/Region/selectors/hasRegionAccess';

type RegionTargetFamily = PreferenceFamily.ABS | PreferenceFamily.EM;

export const selectAccessibleMappedRegionIds = (
  preferences: Preferences | null,
  accessControl: IRegionAccessControl,
  targetFamily: RegionTargetFamily,
): string[] => CrossProductMappingService
  .mapForTarget(preferences, targetFamily)
  .filter((regionId) => hasRegionAccess(regionId, accessControl));

export const createRegionOrMarketAccessPredicate = (
  accessControl: IRegionAccessControl,
) => (regionOrMarketId: string): boolean => {
  const regionId = Market.from(regionOrMarketId)?.belongsToRegion()?.getId() ?? regionOrMarketId;

  return hasRegionAccess(regionId, accessControl);
};

export const selectAccessibleMappedMarketIds = (
  preferences: Preferences | null,
  accessControl: IRegionAccessControl,
): string[] => CrossProductMappingService
  .mapForTarget(preferences, PreferenceFamily.CORPORATE)
  .filter(createRegionOrMarketAccessPredicate(accessControl));

export const toRegionAbbreviations = (regionIds: string[]): string => regionIds
  .map((regionId) => Region.getAbbreviation(regionId))
  .filter(Boolean)
  .join(',');

export const toMarketAbbreviations = (marketIds: string[]): string => marketIds
  .map((marketId) => Market.getAbbreviation(marketId))
  .filter(Boolean)
  .join(',');

const MARKET_ID_BY_ABBREVIATION: Record<string, string> = MarketIdByAbbreviation;

export const toMarketIds = (markets: string): string[] => markets
  .split(',')
  .map((abbreviation) => MARKET_ID_BY_ABBREVIATION[abbreviation])
  .filter(Boolean);

export const toSegmentAbbreviations = (segmentIds: string[]): string => segmentIds
  .map((segmentId) => SegmentTypeById[segmentId])
  .filter(Boolean)
  .join(',');

/**
 * The segments saved for the row this page represents, narrowed to what the page's markets
 * actually offer. Empty when the saved selection owns no row here, so the caller keeps its default.
 */
export const selectScopedSegmentIds = (
  preferences: Preferences | null,
  scope: ISegmentScope,
  marketIds: string[],
): string[] => {
  const saved = preferences?.getMarketSegmentIds(scope.id) ?? [];

  return getVisibleSegmentIds(scope, marketIds).filter((segmentId) => saved.includes(segmentId));
};
